import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Layout } from '../../components/common/Layout';
import {
  SalesLaborKPICards,
  HourlyBreakdownCard,
  SourcesOfSalesCard,
  ClockedInStaffCard,
  DailyTargetsSectionCard,
} from '../../components/SalesLabor';
import { PeriodPicker, type PeriodPickerValue } from '../../components/SalesTrend';
import SalesAndLaborIcon from '@assets/icons/sales_and_labor.svg?react';
import DollarIcon from '@assets/icons/dollar.svg?react';
import ActualLaborCostIcon from '@assets/icons/actual_labor_cost.svg?react';
import TotalHoursIcon from '@assets/icons/total_hours.svg?react';
import SalesPerManHourIcon from '@assets/icons/sales_per_man_hour.svg?react';
import NoOfTransactionsIcon from '@assets/icons/no_of_transactions.svg?react';
import AverageCheckIcon from '@assets/icons/average_check.svg?react';
import TotalDiscountsIcon from '@assets/icons/total_discounts.svg?react';
import {
  commandCenterService,
  type SalesLaborKPIsData,
  type HourlyBreakdownData,
  type TimesheetRow,
} from '../../services/commandCenter.service';
import { goalService } from '../../services/goal.service';
import type { Goal } from '../../types';
import {
  selectCurrentLocation,
  selectIsMultiLocationView,
  selectLocationApiParams,
} from '../../store/locationSelectors';
import { hasLocationSelection } from '../../utils/locationSelectionHelpers';
import { SelectLocationPrompt } from '../../components/common/SelectLocationPrompt';
import { useCanAccessComponent } from '../../hooks/useCanAccessComponent';
import {
  formatCurrency,
  getSalesLaborKpiMetrics,
  buildDailyTargetsItems,
  resolvePeriodDateBounds,
  isSingleDayPeriod,
  getPeriodLabel,
} from '../../utils/salesLaborDetailsHelpers';
import { buildSalesLaborKPIItems } from '../../utils/salesLaborKpiBuilder';

const PAGE_ID = 'sales-labor-detail';

const defaultPeriod: PeriodPickerValue = { periodType: 'today' };

export const SalesLaborDetails = () => {
  const locationApiParams = useSelector(selectLocationApiParams);
  const isMultiLocationView = useSelector(selectIsMultiLocationView);
  const currentLocation = useSelector(selectCurrentLocation);
  const hasLocationScope = hasLocationSelection(locationApiParams);
  const canKpi1 = useCanAccessComponent(PAGE_ID, 'kpi-actual-total-net-sales');
  const canKpi2 = useCanAccessComponent(PAGE_ID, 'kpi-actual-labor-cost');
  const canKpi3 = useCanAccessComponent(PAGE_ID, 'kpi-total-hours');
  const canKpi4 = useCanAccessComponent(PAGE_ID, 'kpi-sales-per-man-hour');
  const canKpi5 = useCanAccessComponent(PAGE_ID, 'kpi-no-of-transactions');
  const canKpi6 = useCanAccessComponent(PAGE_ID, 'kpi-average-check');
  const canKpi7 = useCanAccessComponent(PAGE_ID, 'kpi-total-discounts');
  const canKpi8 = useCanAccessComponent(PAGE_ID, 'kpi-total-refunds');
  const canHourly = useCanAccessComponent(PAGE_ID, 'hourly-breakdown');
  const canSources = useCanAccessComponent(PAGE_ID, 'sources-of-sales');
  const canStaff = useCanAccessComponent(PAGE_ID, 'staff-timesheet');
  const canDaily = useCanAccessComponent(PAGE_ID, 'daily-targets-vs-actual');

  const shouldFetch =
    canKpi1 || canKpi2 || canKpi3 || canKpi4 || canKpi5 || canKpi6 || canKpi7 || canKpi8 ||
    canHourly || canSources || canStaff || canDaily;

  const [period, setPeriod] = useState<PeriodPickerValue>(defaultPeriod);

  const kpiMetrics = useMemo(
    () =>
      getSalesLaborKpiMetrics({
        canKpi1,
        canKpi2,
        canKpi3,
        canKpi4,
        canKpi5,
        canKpi6,
        canKpi7,
        canKpi8,
        canSources,
        canDaily,
      }),
    [canKpi1, canKpi2, canKpi3, canKpi4, canKpi5, canKpi6, canKpi7, canKpi8, canSources, canDaily]
  );

  const [kpis, setKpis] = useState<SalesLaborKPIsData | null>(null);
  const [hourlyBreakdown, setHourlyBreakdown] = useState<HourlyBreakdownData | null>(null);
  const [goals, setGoals] = useState<Goal | null>(null);
  const [loading, setLoading] = useState(hasLocationScope && shouldFetch);
  const [error, setError] = useState<string | null>(null);
  const [timesheetRows, setTimesheetRows] = useState<TimesheetRow[]>([]);
  const [timesheetLoading, setTimesheetLoading] = useState(hasLocationScope && canStaff);

  const periodIsCustomIncomplete =
    period.periodType === 'custom' && (!period.periodStart || !period.periodEnd);

  useEffect(() => {
    if (!hasLocationScope || !shouldFetch || periodIsCustomIncomplete) {
      setKpis(null);
      setHourlyBreakdown(null);
      setGoals(null);
      setError(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const periodOptions = {
      periodType: period.periodType,
      ...(period.periodType === 'custom' && period.periodStart && period.periodEnd
        ? { periodStart: period.periodStart, periodEnd: period.periodEnd }
        : {}),
    };
    const promises: Promise<unknown>[] = [];
    if (kpiMetrics.length > 0) {
      promises.push(
        commandCenterService.getSalesLaborKPIs(locationApiParams, {
          metrics: kpiMetrics,
          ...periodOptions,
          signal: controller.signal,
        }),
      );
    } else {
      promises.push(Promise.resolve(null));
    }
    if (canHourly) {
      promises.push(
        commandCenterService.getHourlyBreakdown(locationApiParams, {
          ...periodOptions,
          signal: controller.signal,
        }),
      );
    } else {
      promises.push(Promise.resolve(null));
    }
    if (canDaily) {
      const tz = currentLocation?.timezone ?? 'UTC';
      const bounds = resolvePeriodDateBounds(period, tz);
      if (bounds) {
        if (bounds.start === bounds.end) {
          promises.push(
            goalService.getResolved(locationApiParams, bounds.start, { signal: controller.signal }).catch((err) => {
              if (controller.signal.aborted) throw err;
              return null;
            }),
          );
        } else {
          promises.push(
            goalService
              .getResolvedRange(locationApiParams, bounds.start, bounds.end, { signal: controller.signal })
              .catch((err) => {
                if (controller.signal.aborted) throw err;
                return null;
              }),
          );
        }
      } else {
        promises.push(Promise.resolve(null));
      }
    } else {
      promises.push(Promise.resolve(null));
    }
    Promise.all(promises)
      .then(([kpisData, hourlyData, goalsData]) => {
        setKpis((kpisData as SalesLaborKPIsData) ?? null);
        setHourlyBreakdown((hourlyData as HourlyBreakdownData) ?? null);
        setGoals((goalsData as Goal | null) ?? null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load Sales & Labor data');
        setKpis(null);
        setHourlyBreakdown(null);
        setGoals(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [
    locationApiParams,
    hasLocationScope,
    shouldFetch,
    canHourly,
    canDaily,
    kpiMetrics.join(','),
    period.periodType,
    period.periodStart,
    period.periodEnd,
    periodIsCustomIncomplete,
    currentLocation?.timezone,
  ]);

  useEffect(() => {
    if (!hasLocationScope || !canStaff || periodIsCustomIncomplete) {
      setTimesheetRows([]);
      setTimesheetLoading(false);
      return;
    }
    const controller = new AbortController();
    setTimesheetLoading(true);
    const periodOptions = {
      periodType: period.periodType,
      ...(period.periodType === 'custom' && period.periodStart && period.periodEnd
        ? { periodStart: period.periodStart, periodEnd: period.periodEnd }
        : {}),
    };
    commandCenterService
      .getTimesheet(locationApiParams, { ...periodOptions, signal: controller.signal })
      .then(setTimesheetRows)
      .catch(() => {
        if (!controller.signal.aborted) setTimesheetRows([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setTimesheetLoading(false);
      });
    return () => controller.abort();
  }, [
    locationApiParams,
    hasLocationScope,
    canStaff,
    period.periodType,
    period.periodStart,
    period.periodEnd,
    periodIsCustomIncomplete,
  ]);

  const periodLabel = useMemo(
    () => getPeriodLabel(period, currentLocation?.timezone ?? 'UTC'),
    [period.periodType, period.periodStart, period.periodEnd, currentLocation?.timezone]
  );

  const salesLaborKPIs = useMemo(
    () =>
      buildSalesLaborKPIItems({
        kpis,
        loading,
        canKpi1,
        canKpi2,
        canKpi3,
        canKpi4,
        canKpi5,
        canKpi6,
        canKpi7,
        canKpi8,
        periodLabel,
        icons: {
          dollar: <DollarIcon className="w-7 h-7 md:w-8 md:h-8 2xl:w-9 2xl:h-9 text-white" />,
          actualLaborCost: <ActualLaborCostIcon className="w-7 h-7 md:w-8 md:h-8 2xl:w-9 2xl:h-9 text-white" />,
          totalHours: <TotalHoursIcon className="w-7 h-7 md:w-8 md:h-8 2xl:w-9 2xl:h-9 text-white" />,
          salesPerManHour: <SalesPerManHourIcon className="w-7 h-7 md:w-8 md:h-8 2xl:w-9 2xl:h-9 text-white" />,
          noOfTransactions: <NoOfTransactionsIcon className="w-7 h-7 md:w-8 md:h-8 2xl:w-9 2xl:h-9 text-white" />,
          averageCheck: <AverageCheckIcon className="w-7 h-7 md:w-8 md:h-8 2xl:w-9 2xl:h-9 text-white" />,
          totalDiscounts: <TotalDiscountsIcon className="w-7 h-7 md:w-8 md:h-8 2xl:w-9 2xl:h-9 text-white" />,
          totalRefunds: <DollarIcon className="w-7 h-7 md:w-8 md:h-8 2xl:w-9 2xl:h-9 text-white" />,
        },
      }),
    [kpis, loading, canKpi1, canKpi2, canKpi3, canKpi4, canKpi5, canKpi6, canKpi7, canKpi8, periodLabel]
  );

  const dailyTargetsItems = useMemo(
    () => buildDailyTargetsItems(kpis, goals),
    [kpis, goals]
  );

  const sourcesSubtitle = periodLabel;

  const dailyTargetsSuffix = useMemo(() => {
    const tz = currentLocation?.timezone ?? 'UTC';
    const singleDay = isSingleDayPeriod(period, tz);
    if (isMultiLocationView) {
      return singleDay ? '(Avg goal)' : '(Avg goal, period total)';
    }
    return singleDay ? undefined : '(Period total)';
  }, [isMultiLocationView, period.periodType, period.periodStart, period.periodEnd, currentLocation?.timezone]);

  return (
    <Layout>
      <div className="p-6 min-h-[200px]">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-base md:text-lg 2xl:text-xl font-semibold text-primary">
            <SalesAndLaborIcon className="w-4 h-4 md:w-5 md:h-5 2xl:w-6 2xl:h-6 text-primary" aria-hidden />
            Sales & Labor Detail
          </h2>
          <div className="flex items-center gap-2">
            <label htmlFor="sales-labor-period" className="text-xs md:text-sm text-secondary">
              Period:
            </label>
            <PeriodPicker
              id="sales-labor-period"
              value={period}
              onChange={setPeriod}
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-negative mb-4" role="alert">{error}</p>
        )}

        {/* Everything below is location data; with no selection it would render
            "—" KPIs and "No timesheet data", so show the prompt instead. */}
        {!hasLocationScope ? (
          <SelectLocationPrompt subject="Sales &amp; Labor data" />
        ) : (
          <>
        {salesLaborKPIs.length > 0 && <SalesLaborKPICards items={salesLaborKPIs} />}

        {(canHourly || canSources) && (
          <div
            className={
              canHourly && canSources
                ? 'grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6'
                : 'grid grid-cols-1 gap-6 mb-6'
            }
          >
            {canHourly && (
              <HourlyBreakdownCard
                xAxisLabels={hourlyBreakdown?.labels ?? []}
                salesData={hourlyBreakdown?.netSalesPerHour ?? []}
                laborCostData={hourlyBreakdown?.laborCostPercentPerHour ?? []}
                height={380}
                className={canSources ? 'lg:col-span-2' : ''}
                loading={loading}
              />
            )}
            {canSources && (
              <SourcesOfSalesCard
                totalSales={
                  kpis?.actualTotalSales == null
                    ? '—'
                    : formatCurrency(kpis.actualTotalSales)
                }
                segments={kpis?.sourcesOfSales ?? []}
                subtitle={sourcesSubtitle}
                loading={loading}
              />
            )}
          </div>
        )}

        {(canStaff || canDaily) && (
          <div
            className={
              canStaff && canDaily
                ? 'grid grid-cols-1 lg:grid-cols-3 gap-6'
                : 'grid grid-cols-1 gap-6'
            }
          >
            {canStaff && (
              <ClockedInStaffCard
                rows={timesheetRows}
                loading={timesheetLoading}
                className={canDaily ? 'lg:col-span-2' : ''}
                periodLabel={periodLabel}
                groupByLocation={isMultiLocationView}
              />
            )}
            {canDaily && (
              <DailyTargetsSectionCard
                items={dailyTargetsItems}
                loading={loading}
                titleSuffix={dailyTargetsSuffix}
              />
            )}
          </div>
        )}
          </>
        )}
      </div>
    </Layout>
  );
};
